import React, {useEffect, useMemo, useState} from 'react';
import {useTaopix} from '../Components/Context/TaopixContext';
import {useUiState} from '../Components/Context/UserInterface/UserInterfaceContext';
import {ShuffleLayoutButtonState} from '../Components/Page/Actions/AutoLayoutButton';
import {PageAPI, PageStyleType} from '@taopix/taopix-core';
import {useSelector} from 'react-redux';
import {LayoutSelector} from '../Selectors';

const useShuffleLayoutButtons = (): [
  ShuffleLayoutButtonState,
  React.Dispatch<React.SetStateAction<ShuffleLayoutButtonState>>,
  PageAPI
] => {
  const app = useTaopix();
  const ui = useUiState();
  const layout = useSelector(LayoutSelector);

  const [buttonState, setButtonState] = useState<ShuffleLayoutButtonState>({
    left: false,
    spread: false,
    right: false,
  });

  const api = useMemo(() => {
    return app.core.get('instances').get(ui.state.pageNumber);
  }, [ui.state.pageNumber]);

  useEffect(() => {
    if (!api) {
      return;
    }

    // if we're a cover, no buttons are available
    if (api.getDisplayProps().pageIsCover) {
      setButtonState({
        left: false,
        spread: false,
        right: false,
      });
      return void 0;
    }

    // set everything to true
    const tempState = {
      left: true,
      spread: true,
      right: true,
    };

    tempState.spread =
      api.getDisplayProps().pageIsSpreads &&
      (layout.shuffleLayouts?.some(layout => PageStyleType.Spread === layout.type) || false);

    if (api.insideLeft()) {
      tempState.spread = false;
      tempState.left = false;
      tempState.right =
        layout.shuffleLayouts?.some(layout => PageStyleType.Single === layout.type) || false;
    }

    if (api.outsideRight()) {
      tempState.spread = false;
      tempState.left =
        layout.shuffleLayouts?.some(layout => PageStyleType.Single === layout.type) || false;
      tempState.right = false;
    }

    // For single page we dont need any available options
    if (!api.getDisplayProps().pageIsSpreads) {
      tempState.spread = false;
      tempState.left = false;
      tempState.right = false;
    }

    setButtonState(tempState);
  }, [api]);

  return [buttonState, setButtonState, api as PageAPI];
};

export default useShuffleLayoutButtons;
